import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Users, Play, Tv } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function DiscoveryFeed({ loggedOut }) {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [parties, setParties] = useState([]);
  const [slots, setSlots] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (loggedOut) { setParties([]); setLoaded(true); return; }
    try {
      const d = await authFetch("/parties/discover");
      setParties(d.parties || []);
    } catch (e) {
      // silent — feed is best-effort
    } finally {
      setLoaded(true);
    }
    authFetch("/slots").then(setSlots).catch(() => {});
  }, [authFetch, loggedOut]);

  const joinParty = useCallback(async (code) => {
    try { const s = await authFetch("/slots/consume", { method: "POST" }); setSlots(s); } catch (e) { /* soft */ }
    navigate(`/room/${code}`);
  }, [authFetch, navigate]);

  useEffect(() => {
    load();
    if (loggedOut) return;
    const t = setInterval(load, 12000); // live-ish refresh
    return () => clearInterval(t);
  }, [load, loggedOut]);

  if (!loaded) return null;

  return (
    <div className="mt-8 lg:mt-0" data-testid="discovery-feed">
      <div className="flex items-center gap-2 mb-3">
        <Radio className="w-4 h-4 text-[#FF006E]" style={{ filter: "drop-shadow(0 0 6px #FF006E)" }} />
        <p className="text-xs font-display font-bold uppercase tracking-widest neon-pink">What's playing now</p>
        <span className="flex-1 h-px bg-gradient-to-l from-transparent to-[#FF006E]/40" />
        {parties.length > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-[#00FF66] font-mono-tech" data-testid="feed-live-dot">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse" style={{ boxShadow: "0 0 6px #00FF66" }} /> LIVE
          </span>
        )}
      </div>

      {slots && !slots.premium && (
        <div data-testid="slots-badge" className="flex items-center justify-between gap-2 mb-3 rounded-xl border border-[#9D4EDD]/30 bg-[#9D4EDD]/8 px-3 py-2">
          <span className="text-xs text-[#C9B8E8]">
            <span className="font-bold neon-purple">{slots.remaining}</span> of {slots.limit} free join-slots left today
          </span>
          <span className="text-[10px] uppercase tracking-wide text-[#88A0C0]">Free plan</span>
        </div>
      )}
      {slots && slots.premium && (
        <div data-testid="slots-badge-premium" className="flex items-center gap-1.5 mb-3 text-xs neon-cyan font-bold">
          <Radio className="w-3 h-3" /> Premium · unlimited joins
        </div>
      )}

      {parties.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-[#0A0E27]/60 p-6 text-center" data-testid="feed-empty">
          {loggedOut ? (
            <>
              <p className="text-sm text-[#88A0C0]">Log in to see what your friends are watching.</p>
              <p className="text-xs text-[#5a6a8a] mt-1">Their live parties show up here — tap once to jump in. ✨</p>
            </>
          ) : (
            <>
              <p className="text-sm text-[#88A0C0]">None of your friends are watching right now.</p>
              <p className="text-xs text-[#5a6a8a] mt-1">Start a party to see it here! ✨</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="feed-list">
          {parties.map((p) => (
            <button
              key={p.roomCode}
              data-testid={`feed-party-${p.roomCode}`}
              onClick={() => joinParty(p.roomCode)}
              className="group w-full flex items-center gap-3 rounded-2xl border border-[#00D9FF]/25 bg-[#0A0E27]/70 backdrop-blur-md p-3 text-left transition-all hover:border-[#00D9FF] hover:shadow-[0_0_24px_rgba(0,217,255,0.35)]"
            >
              <span className="w-11 h-11 shrink-0 rounded-xl bg-[#00D9FF]/12 border border-[#00D9FF]/40 flex items-center justify-center text-xl">
                {p.friends[0]?.avatarEmoji || "🎬"}
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1.5 text-sm text-[#F0F6FF] font-semibold truncate">
                  <span className="neon-cyan truncate">@{p.friends[0]?.username || p.host?.name}</span>
                  <span className="text-[#88A0C0] font-normal">watching</span>
                </span>
                <span className="block text-[13px] text-[#F0F6FF] truncate mt-0.5" title={p.title || ""}>
                  {p.title || "Choosing something…"}
                </span>
                <span className="flex items-center gap-2 mt-1">
                  {p.source && (
                    <span className="flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#9D4EDD]/15 text-[#9D4EDD] border border-[#9D4EDD]/30">
                      <Tv className="w-2.5 h-2.5" /> {p.source}
                    </span>
                  )}
                  <span className="flex items-center gap-0.5 text-[10px] text-[#88A0C0]" data-testid={`feed-viewers-${p.roomCode}`}>
                    <Users className="w-2.5 h-2.5" /> {p.viewers} watching
                  </span>
                </span>
              </span>
              <span className="shrink-0 flex items-center gap-1 h-9 px-4 rounded-full bg-[#00D9FF] text-navy-ink font-display font-bold uppercase text-xs tracking-wider glow-cyan group-hover:scale-105 transition-transform">
                <Play className="w-3.5 h-3.5 fill-current" /> Join
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
